import { ComponentKey, RuleType } from "../../constants/";
import { UUID_LENGTH } from "../../constants/general.constants";
import type { Process } from "../../types";
import { RelatedProcessFlowType } from "../../types";
import { getNanoId } from "../../utils";
import { prefixCopy, suffixTag } from "./dedupe.helpers";
import { v4 as uuid } from "uuid";
import { dedupeComponent } from "./dedupeComponent";
import { devLogger } from "@certa/common/utils/devLogger.ts";

// Here we are only creating duplicate config of the component, then we will create changes to update the process in mutators
export const duplicateComponent = ({
  componentMetaJSON,
  componentId,
  componentType
}: {
  componentMetaJSON: Partial<Process>;
  componentId: string;
  componentType: ComponentKey;
}) => {
  /**
   * Steps:
   * 1. Extract all unique tags
   * 2. Do selective dedupe of tags for special cases (like extras)
   * 3. Do bulk deduping of tags
   * 4. Return the deduped JSON
   */

  if (!componentMetaJSON) throw Error("ComponentJSON not found");

  // Adding a Copy prefix on the component's name
  if (
    componentType === ComponentKey.SWIMLANES &&
    componentMetaJSON?.swimlanes?.[componentId]
  ) {
    componentMetaJSON.swimlanes[componentId] = {
      ...componentMetaJSON.swimlanes[componentId],
      name: prefixCopy(componentMetaJSON.swimlanes[componentId].name)
    };
  } else if (
    componentType === ComponentKey.STEPS &&
    componentMetaJSON?.steps?.[componentId]
  ) {
    componentMetaJSON.steps[componentId] = {
      ...componentMetaJSON.steps[componentId],
      name: prefixCopy(componentMetaJSON.steps[componentId].name)
    };
  } else if (
    componentType === ComponentKey.FIELDS &&
    componentMetaJSON?.fields?.[componentId]
  ) {
    componentMetaJSON.fields[componentId] = {
      ...componentMetaJSON.fields[componentId],
      body: prefixCopy(componentMetaJSON.fields[componentId].body)
    };
  }

  let componentMetaJSONString = JSON.stringify(componentMetaJSON);

  const tagMappings: Record<string, string> = {};
  const externalTagMappings: Record<string, string> = {};
  const idMappings: Record<string, string> = {};

  // Step 1 - Extracting all unique tags
  JSON.parse(componentMetaJSONString, (key, value) => {
    if (key === "tag" && typeof value === "string") {
      tagMappings[value] = suffixTag(value);
    } else if (key === "externalTag" && typeof value === "string") {
      externalTagMappings[value] = suffixTag(value);
    } else if (
      key === "id" &&
      typeof value === "string" &&
      !idMappings[value]
    ) {
      const valueSplit = value.split("__");
      if (
        valueSplit.length > 1 &&
        valueSplit[valueSplit.length - 1].length === UUID_LENGTH
      ) {
        // This is definitely UUID and we should replace it
        // highly unlikely that this is something hardcoded by someone
        // BUT this can be a potential issue as there are other places where UUID is used, but
        // not in this format. Like editable table uses UUID as well.
        // But because this implementation will reduce the complexity significantly,
        // we'll go with it.
        valueSplit.splice(valueSplit.length - 1, 1, uuid());

        idMappings[value] = valueSplit.join("__");
      } else {
        // This is for webGL created components having nanoIds.
        valueSplit.splice(valueSplit.length - 1, 1, getNanoId());
        idMappings[value] = valueSplit.join("__");
      }
    }
    return value;
  });

  // Step 2 - Deduping special cases like C2P/P2C
  const patch: Partial<Process> = {
    rules: {},
    conditionGroups: {},
    conditions: {}
  };

  Object.values(componentMetaJSON?.rules || {}).forEach(rule => {
    let conditionIds: string[] = [];
    // let conditionGroupIds:string[] = [];

    if (rule.ruleType === RuleType.RelatedProcessDataInheritance) {
      // Now we prepare the patch for conditions

      const topLevelGroup =
        componentMetaJSON?.conditionGroups?.[rule.topLevelGroupId];
      if (!topLevelGroup) {
        // should be unreachable ideally
        throw new Error("Top level group ID not found");
      }

      let remainingConditionGroupIds = [topLevelGroup.id];
      while (remainingConditionGroupIds.length > 0) {
        const tmpArray = [...remainingConditionGroupIds];

        // Save the condition groups that we've already handled
        // conditionGroupIds = [...conditionGroupIds,...remainingConditionGroupIds];

        // and clear the remaining array (so that we can add more to it, if needed)
        remainingConditionGroupIds = [];

        for (const conditionGroupId of tmpArray) {
          const conditionGroup =
            componentMetaJSON.conditionGroups?.[conditionGroupId];
          if (!conditionGroup) return;
          patch.conditionGroups = {
            ...patch.conditionGroups,
            [idMappings[conditionGroupId]]: {
              ...conditionGroup,
              id: idMappings[conditionGroupId],
              groups: conditionGroup.groups.map(groupId => idMappings[groupId]),
              conditions: conditionGroup.conditions.map(
                conditionId => idMappings[conditionId]
              )
            }
          };

          delete componentMetaJSON.conditionGroups?.[conditionGroupId];

          remainingConditionGroupIds = [
            ...remainingConditionGroupIds,
            ...conditionGroup.groups
          ];
          conditionIds = [...conditionIds, ...conditionGroup.conditions];
        }
      }

      if (rule.actions.flow_type === RelatedProcessFlowType.CHILD_TO_PARENT) {
        // source_field should be replaced with deduped tag
        // target_field should be left as it-is.
        // first_compare_field should be left as it-is
        const sourceField = rule.actions.source_field;

        // first let's prepare patch for the rule itself
        patch.rules = {
          ...patch.rules,
          [idMappings[rule.id]]: {
            ...rule,
            id: idMappings[rule.id], // replacing IDs
            topLevelGroupId: idMappings[rule.topLevelGroupId], // replacing IDs
            actions: {
              ...rule.actions,
              source_field: tagMappings[sourceField] || sourceField
            },
            __targetFieldTag: rule.__targetFieldTag
              ? tagMappings[rule.__targetFieldTag]
              : undefined
          }
        };

        delete componentMetaJSON.rules?.[rule.id];

        conditionIds.forEach(conditionId => {
          const condition = componentMetaJSON.conditions?.[conditionId];
          if (!condition) return;
          patch.conditions = {
            ...patch.conditions,
            [idMappings[conditionId]]: {
              ...condition,
              id: idMappings[conditionId], // Replacing ID,
              firstCompareField:
                tagMappings[condition.firstCompareField] ||
                condition.firstCompareField
            }
          };

          delete componentMetaJSON.conditions?.[conditionId];
        });
      } else if (
        rule.actions.flow_type === RelatedProcessFlowType.PARENT_TO_CHILD
      ) {
        // source_field should be left as it-is.
        // target_field should be replaced with deduped tags.
        // first_compare_field should be replaced with deduped tags.
        const targetField = rule.actions.target_field;
        patch.rules = {
          ...patch.rules,
          [idMappings[rule.id]]: {
            ...rule,
            id: idMappings[rule.id], // replacing IDs
            topLevelGroupId: idMappings[rule.topLevelGroupId], // replacing IDs
            actions: {
              ...rule.actions,
              target_field: tagMappings[targetField] || targetField
            },
            __targetFieldTag: rule.__targetFieldTag
              ? tagMappings[rule.__targetFieldTag]
              : undefined
          }
        };

        delete componentMetaJSON.rules?.[rule.id];

        conditionIds.forEach(conditionId => {
          const condition = componentMetaJSON.conditions?.[conditionId];
          if (!condition) return;
          patch.conditions = {
            ...patch.conditions,
            [idMappings[conditionId]]: {
              ...condition,
              id: idMappings[conditionId] // Replacing ID
            }
          };
          delete componentMetaJSON.conditions?.[conditionId];
        });
      }
    }
  });
  // Recreating the stringified meta JSON because
  // we did some special handling above and got rid of
  // certain keys
  componentMetaJSONString = JSON.stringify(componentMetaJSON);
  devLogger.log(componentMetaJSONString);

  // Step 3 - Bulk Deduping
  let dedupedJSON: typeof componentMetaJSON = dedupeComponent({
    jsonString: componentMetaJSONString,
    tagMappings,
    externalTagMappings,
    idMappings
  });

  if (!dedupedJSON) return; // there was an error while deduping JSON

  dedupedJSON = {
    ...dedupedJSON,
    rules: {
      ...dedupedJSON.rules,
      ...patch.rules
    },
    conditionGroups: {
      ...dedupedJSON.conditionGroups,
      ...patch.conditionGroups
    },
    conditions: {
      ...dedupedJSON.conditions,
      ...patch.conditions
    }
  };
  devLogger.log("dedupedJSON", dedupedJSON);

  // Step 4 - Return the deduped JSON
  return {
    originalComponentId: componentId,
    duplicatedComponentId: idMappings[componentId],
    metaJSON: dedupedJSON
  };
};
